import { useState } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  createFilterOptions
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useLabFieldValues } from '@api/bff/labField';
import { useCreateLabFieldValue, labFieldKeys } from '@api/bff/labField';

type OptionType = {
  inputValue?: string;
  label: string;
};

const filter = createFilterOptions<OptionType>();

type Props = {
  value: string;
  onChange: (value: string) => void;
};

export const ActionAutocomplete = ({ value, onChange }: Props) => {
  const queryClient = useQueryClient();
  const { data: options = [], isLoading } = useLabFieldValues();
  const createMutation = useCreateLabFieldValue();
  const [inputValue, setInputValue] = useState('');

  const optionObjects: OptionType[] = options.map(o => ({ label: o }));

  const selectedOption: OptionType | null = value ? { label: value } : null;

  const handleChange = async (
    _event: React.SyntheticEvent,
    newValue: string | OptionType | null
  ) => {
    if (newValue === null) {
      onChange('');
      return;
    }

    if (typeof newValue === 'string') {
      onChange(newValue);
      return;
    }

    if (newValue.inputValue) {
      const newVal = newValue.inputValue;
      await createMutation.mutateAsync(newVal);
      queryClient.setQueryData<string[]>([...labFieldKeys.all], old =>
        [...(old ?? []), newVal].sort((a, b) => a.localeCompare(b))
      );
      onChange(newVal);
    } else {
      onChange(newValue.label);
    }
  };

  return (
    <Autocomplete
      freeSolo
      value={selectedOption}
      inputValue={inputValue}
      onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
      onChange={handleChange}
      options={optionObjects}
      loading={isLoading}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.label
      }
      isOptionEqualToValue={(option, val) => option.label === val.label}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const { inputValue: iv } = params;
        const isExisting = opts.some(o => iv === o.label);
        if (iv !== '' && !isExisting) {
          filtered.push({
            inputValue: iv,
            label: `Add "${iv}"`
          });
        }
        return filtered;
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Action"
          size="small"
          placeholder="e.g. AO1 DISABLE, set-output, identify"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};
